import classNames from "classnames"
import { useEffect, useState } from "react"
import { Operation } from "../../../../types"
import { Button } from "../../../components/button/Button"
import { StatusText } from "../../../components/status/Status"
import { getOperation } from "../../../fetch/projects"
import { formatTime } from "../../../lib/formatTime"
import { formatTimeDelta } from "../../../lib/formatTimeDelta"
import styles from "../AdminTasks/sidepanel.module.css"
import { useWatcher } from "../../../lib/state/useWatcher"
import { tenantState } from "../../../store/tenant"
import { formatDate } from "@/lib/formatDate"

type Props = {
  operation?: Operation
  onClose: () => void
  subProjectId?: string
  projectId?: string
}

type OpTask = {
  unitsUsed: number
  salaryUsed: number
  createdAt: string
  id: number
  timeUsed: number
  status: number
  comment?: string
  deletedAt?: string
  deletedComment?: string
  user?: {
    name: string
    id: string
  }
  date: string
}

type OpRes = {
  expenseSum: number
  id: number
  name: string
  status: number
  successor_id: number
  tasks: OpTask[]
  timePredicted: number
  timeUsed: number
  unitsPredicted: number
  unitsUsed: number
  workerCount: number
}
export const OpenOperation = ({
  operation,
  onClose,
  subProjectId,
  projectId,
}: Props) => {
  const {
    settings: { features },
  } = useWatcher(tenantState)
  const [op, setOp] = useState<OpRes>()

  const formatUnits = (unit?: number, asUnits?: boolean) => {
    if (!unit) {
      return 0
    }
    return asUnits ? unit : `${(unit / 100).toFixed(2)}€`
  }

  useEffect(() => {
    if (subProjectId && projectId && operation?.id) {
      getOperation(projectId, subProjectId, operation?.id).then(async (res) => {
        const json = await res.json()
        setOp(json)
      })
    }
  }, [operation?.id, projectId, subProjectId])

  const blocksContainerClass =
    features["units"] && features["salary"] && features["time"]
      ? "blocks3"
      : "blocks"

  const hasAnyPlannedDates =
    operation?.plannedAt != null || operation?.plannedTo != null

  return (
    <>
      <div
        className={classNames(styles.sidepanel, operation ? styles.open : "")}
      >
        <div className={styles.wrap}>
          <div className={styles.close}>
            <Button variant="inline" onClick={onClose}>
              Aizvērt
            </Button>
          </div>
          <div className={styles.title}>{operation?.name}</div>
          <StatusText status={operation?.status} />
          {hasAnyPlannedDates && (
            <div className="text-gray-500 text-[14px] mt-4">
              Plānots:{" "}
              {operation?.plannedAt ? formatDate(operation.plannedAt, "dd.MMM yyyy") + " " : ""}
              {operation?.plannedTo ? "līdz " + formatDate(operation.plannedTo, "dd.MMM yyyy") : ""}
            </div>
          )}
          <div className={styles[blocksContainerClass]}>
            {features["units"] && (
              <div className={styles.taskBlock}>
                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatUnits(operation?.unitsUsed, true)}
                  </div>
                  <div className={styles.taskRowSub}>Vienības</div>
                </div>
                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatUnits(operation?.unitsPredicted, true)}
                  </div>
                  <div className={styles.taskRowSub}>Paredzētās vienības</div>
                </div>
              </div>
            )}
            {features["salary"] && (
              <div className={styles.taskBlock}>
                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatUnits(operation?.salaryUsed)}
                  </div>
                  <div className={styles.taskRowSub}>Nopelnītais</div>
                </div>

                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatUnits(operation?.salaryPredicted)}
                  </div>
                  <div className={styles.taskRowSub}>Summa</div>
                </div>
              </div>
            )}
            {features["time"] && (
              <div className={styles.taskBlock}>
                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatTimeDelta(operation?.timeUsed)}
                  </div>
                  <div className={styles.taskRowSub}>Reālais laiks</div>
                </div>
                <div className={styles.taskRow}>
                  <div className={styles.taskRowTitle}>
                    {formatTimeDelta(operation?.timePredicted)}
                  </div>
                  <div className={styles.taskRowSub}>Paredzētais laiks</div>
                </div>
              </div>
            )}
          </div>

          {op && (
            <table className="main">
              <thead>
                <tr>
                  <td>Statuss</td>
                  <td>Darbinieks</td>
                  <td>Pavadītais laiks</td>
                  {features["units"] && <td>Vienības</td>}
                  {features["salary"] && <td>Nopelnītais</td>}
                  <td>Datums</td>
                  <td>Izveidots</td>
                </tr>
              </thead>
              <tbody>
                {op.tasks.map((task) => (
                  <tr key={task.id} style={task.deletedAt ? { opacity: 0.5 } : undefined}>
                    <td>
                      {task.deletedAt ? (
                        <span style={{ color: "var(--error)" }}>
                          Dzēsts{task.deletedComment ? `: ${task.deletedComment}` : ""}
                        </span>
                      ) : task.status === 1 ? (
                        <span style={{ color: "var(--success)" }}>Apstiprināts</span>
                      ) : (
                        <span>Nav apstiprināts</span>
                      )}
                    </td>
                    <td>{task.user?.name ?? "-"}</td>
                    <td>{formatTimeDelta(task.timeUsed)}</td>
                    {features["units"] && (
                      <td>{formatUnits(task.unitsUsed, true)}</td>
                    )}
                    {features["salary"] && (
                      <td>{formatUnits(task.salaryUsed)}</td>
                    )}
                    <td>
                      {task.date ? formatDate(task.date, "dd.MM.yyyy") : "-"}
                    </td>
                    <td>{formatTime(task.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}
